/**
 * v1.4.108 Bong 精选展示栏前插布局（上传作品功能）——即梦式流式版
 *
 * 布局按容器实测宽动态计算（WorksSection 以 ResizeObserver 实测容器宽 →
 * 换算列数/列宽后传入），不再依赖 1400 定宽画布缩放：
 * - 列数断点（WorksSection 计算）：容器 ≥1060 → 4 列 / ≥760 → 3 列 /
 *   ≥460 → 2 列 / 其余 1 列；列宽 = (容器宽 − 间距×(列数−1)) / 列数，
 *   卡片宽度随列宽伸缩，字号恒定（即梦式：内容流式、文字不缩放）
 * - 卡高按比例换算（基准 = 设计稿 340 宽）：图像卡 h × colW/340（错落构图保持）；
 *   视频卡 = 封面（192×colW/340）+ 固定 78px 文字区（封面底 16 标题、+42 头像行）
 * - 图像作品：依次放入当前最矮列；用户作品（统一基准 452 高，与设计稿主流卡一致）
 *   排最前 → 展示栏「最新在前」
 * - 视频作品：等高网格行序放置；用户作品排最前
 */

export interface WorkCard {
  key: string;
  /** 作品标识：模板卡 = wf-XX / v-XX；用户作品 = worksStore 的作品 id（点击预览时解析数据用） */
  id?: string;
  /** 显示图（图片作品封面 / 视频封面） */
  src: string;
  /** 基准卡高（340 宽坐标系；实际渲染高 = h × colW/340，瀑布流可变 / 视频恒 270） */
  h: number;
  title: string;
  user: string;
  avatar?: string;
  likes: string;
  /** 视频作品原始地址（展示端无封面时用 video 首帧兜底） */
  videoUrl?: string;
  /** 是否显示「查看创作流程」胶囊（视频作品：公开制作流程才显示；模板卡恒显示） */
  showFlow?: boolean;
}

/** 已放置卡片：实际渲染坐标与尺寸（像素） */
export interface PlacedCard {
  x: number;
  y: number;
  w: number;
  h: number;
}

export const GAP = 12;

/** 瀑布流放置：cols 列、列宽 colW，卡高按 colW/340 等比换算，依次放入当前最矮列 */
export function layoutWaterfall(
  cards: { key: string; h: number }[],
  cols: number,
  colW: number,
): { placed: (PlacedCard & { key: string })[]; canvasH: number } {
  const colHeights = new Array(cols).fill(0);
  const placed = cards.map((card) => {
    const col = colHeights.indexOf(Math.min(...colHeights));
    const h = Math.round(card.h * (colW / 340));
    const item = { key: card.key, x: col * (colW + GAP), y: colHeights[col], w: colW, h };
    colHeights[col] += h + GAP;
    return item;
  });
  return { placed, canvasH: Math.max(...colHeights) - GAP };
}

/** 等高网格行序放置：cellH 由调用方换算（视频 = 封面高 + 78 文字区），按 index 对位 */
export function layoutGrid(
  count: number,
  cols: number,
  colW: number,
  cellH: number,
): { placed: (PlacedCard & { index: number })[]; canvasH: number } {
  const placed = Array.from({ length: count }, (_, index) => ({
    index,
    x: (index % cols) * (colW + GAP),
    y: Math.floor(index / cols) * (cellH + GAP),
    w: colW,
    h: cellH,
  }));
  const rows = Math.max(1, Math.ceil(count / cols));
  return { placed, canvasH: rows * (cellH + GAP) - GAP };
}
